/**
 * Photography used in the app.
 *
 * Every image comes from the NTT DATA corporate library and is subject to its
 * licence terms — these are stock assets bought for corporate communications,
 * and a public lead-generation site is a broader use than an internal deck.
 * Marketing needs to confirm the licence covers it before this goes to prod.
 * Provenance is recorded here so that conversation can happen against facts.
 *
 * Derivatives are produced by web/scripts/build-images.sh and committed under
 * public/assets/images/. Do not hand-edit them.
 *
 * CI rules applied when selecting (see ci_bildsprache.md): naturally lit,
 * slightly blue rather than warm, no black and white, no digital overlays, no
 * staged smiling-at-camera, diversity represented.
 */

export interface AppImage {
  /** Base name of the derivative set in public/assets/images/. */
  readonly slug: string;
  /** Widths generated, ascending. Drives the srcset. */
  readonly widths: readonly number[];
  /** Intrinsic aspect ratio of the master, for reserving layout space. */
  readonly aspect: number;
  /** Master file, relative to the corporate Bilderdatenbank root. */
  readonly source: string;
  /**
   * Empty string marks the image as decorative: it carries no information the
   * surrounding copy does not already state, so a screen reader should skip it
   * rather than read a description of scenery.
   */
  readonly alt: string;
}

export const SKYLINE: AppImage = {
  slug: 'skyline',
  widths: [640, 1024, 1600],
  aspect: 8014 / 5343,
  source: 'NTT DATA Image Library 2024/04_Abstract/GettyImages-1368629737.jpg',
  alt: '',
};

export const ADVISORY: AppImage = {
  slug: 'advisory',
  widths: [640, 1024, 1600],
  aspect: 2121 / 1414,
  source:
    'IGNITE - Image Library (Retouched)-selected/Trust/Best for Print-Digital-OOH/GettyImages-683773588  edited.jpg',
  alt: '',
};

/** Builds the srcset string for an image set. */
export function srcSet(image: AppImage): string {
  return image.widths.map((w) => `/assets/images/${image.slug}-${w}.webp ${w}w`).join(', ');
}

/** Fallback `src` for the rare browser that ignores srcset. */
export function fallbackSrc(image: AppImage): string {
  return `/assets/images/${image.slug}-${image.widths[1] ?? image.widths[0]}.webp`;
}
